import { EstadoBadge } from "@/components/Badges";
import { eur } from "@/lib/api";
import { Receipt, TrendUp, Coin, Users, CalendarBlank, Clock, ArrowRight } from "@phosphor-icons/react";

const TONES = {
  slate: "text-slate-900", emerald: "text-emerald-600", rose: "text-rose-600",
  blue: "text-blue-600", amber: "text-amber-600",
};

function Kpi({ label, value, tone = "slate", testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 transition-transform hover:-translate-y-0.5 shadow-sm">
      <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 truncate">{label}</div>
      <div className={`mt-2 text-2xl sm:text-3xl font-display font-bold kpi-num ${TONES[tone]}`}>{value}</div>
    </div>
  );
}

export function Overview({ stats, facturas, onOpen, onNavigate }) {
  const recientes = [...facturas].slice(0, 6);
  const desglose = stats?.desglose_iva || {};

  // Formato de fecha actual
  const now = new Date();
  const fechaHoy = now.toLocaleDateString("es-ES", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const fechaHoyCap = fechaHoy.charAt(0).toUpperCase() + fechaHoy.slice(1);

  // Formato de última sincronización
  const formatLastPoll = (iso) => {
    if (!iso) return "Pendiente de primer ciclo";
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) +
        " · " + d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return iso;
    }
  };

  return (
    <div className="animate-fadein space-y-4 sm:space-y-6">
      {/* Barra de Fecha y Estado de Sincronización */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 bg-white p-3.5 sm:p-4 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
            <CalendarBlank size={20} weight="bold" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Fecha Actual</div>
            <div className="text-xs sm:text-sm md:text-base font-bold text-slate-800">{fechaHoyCap}</div>
          </div>
        </div>

        <div className="flex items-center gap-3 border-t sm:border-t-0 pt-2 sm:pt-0 border-slate-100">
          <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
            <Clock size={20} weight="bold" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Último Polling Realizado</div>
            <div className="text-xs sm:text-sm md:text-base font-semibold text-slate-800">{formatLastPoll(stats?.last_polled_at)}</div>
          </div>
        </div>
      </div>

      {/* KPIs principales */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        <Kpi label="Total facturas" value={stats?.total_facturas || 0} testid="kpi-total" />
        <Kpi label="Correctas" value={stats?.ok || 0} tone="emerald" testid="kpi-ok" />
        <Kpi label="Incidencias" value={stats?.incidencias || 0} tone="rose" testid="kpi-incidencias" />
        <Kpi label="Abonos" value={stats?.abonos || 0} tone="blue" testid="kpi-abonos" />
        <Kpi label="Duplicados" value={stats?.duplicados || 0} tone="amber" testid="kpi-duplicados" />
      </div>

      {/* Resumen Contable y Fiscal para Hostelería */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Total Gasto Neto</span>
            <Coin size={18} className="text-slate-400" />
          </div>
          <div className="mt-2 text-xl sm:text-2xl font-display font-bold text-slate-900 kpi-num">
            {eur(stats?.total_gasto_neto || 0)}
          </div>
          <div className="mt-1 text-xs text-slate-400">Facturas aprobadas netas de abonos</div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Total IVA Soportado</span>
            <TrendUp size={18} className="text-slate-400" />
          </div>
          <div className="mt-2 text-xl sm:text-2xl font-display font-bold text-emerald-600 kpi-num">
            {eur(stats?.total_iva_soportado || 0)}
          </div>
          <div className="mt-1 text-xs text-slate-400">Deducible para modelo 303 trimestral</div>
        </div>

        <button
          type="button"
          data-testid="kpi-proveedores-buzon"
          onClick={() => onNavigate?.("proveedores")}
          className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm text-left transition-all hover:border-slate-300 hover:shadow-md hover:-translate-y-0.5 group cursor-pointer w-full"
        >
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 group-hover:text-slate-600 transition-colors">
              Proveedores & Buzón
            </span>
            <div className="flex items-center gap-1 text-slate-400 group-hover:text-slate-700 transition-colors">
              <span className="text-[11px] font-medium hidden group-hover:inline">Ver sección</span>
              <ArrowRight size={16} className="transform group-hover:translate-x-0.5 transition-transform" />
            </div>
          </div>
          <div className="mt-2 text-xl sm:text-2xl font-display font-bold text-slate-800 kpi-num">
            {stats?.proveedores_count || 0} proveedores
          </div>
          <div className="mt-1 text-xs text-slate-400">
            {stats?.emails_procesados || 0} correos procesados ({stats?.pendientes || 0} pendientes)
          </div>
        </button>
      </div>

      {/* Desglose de IVA en 3 tipos */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm">
        <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-700 mb-3">
          Desglose Fiscal de IVA (Hostelería)
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="text-xs font-semibold text-emerald-700">IVA 4% (Superreducido)</div>
            <div className="text-sm mt-1 font-mono text-slate-700">
              Base: {eur(desglose.iva_4?.base || 0)}
            </div>
            <div className="text-xs text-slate-500 font-mono">
              Cuota: {eur(desglose.iva_4?.cuota || 0)}
            </div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="text-xs font-semibold text-blue-700">IVA 10% (Alimentos y Bebidas)</div>
            <div className="text-sm mt-1 font-mono text-slate-700">
              Base: {eur(desglose.iva_10?.base || 0)}
            </div>
            <div className="text-xs text-slate-500 font-mono">
              Cuota: {eur(desglose.iva_10?.cuota || 0)}
            </div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="text-xs font-semibold text-purple-700">IVA 21% (Suministros y Servicios)</div>
            <div className="text-sm mt-1 font-mono text-slate-700">
              Base: {eur(desglose.iva_21?.base || 0)}
            </div>
            <div className="text-xs text-slate-500 font-mono">
              Cuota: {eur(desglose.iva_21?.cuota || 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Lista de últimas facturas procesadas */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 sm:px-5 py-3.5 border-b border-slate-200 text-sm font-semibold text-slate-700">
          Últimas Facturas Procesadas
        </div>
        <div className="divide-y divide-slate-100">
          {recientes.length === 0 && (
            <div className="px-5 py-12 text-sm text-slate-400 text-center">
              <Receipt size={32} className="mx-auto mb-2 text-slate-300" />
              <p className="font-medium text-slate-600">No hay facturas procesadas aún.</p>
              <p className="text-xs mt-1 text-slate-400">Pulsa <strong>«Ejecutar polling ahora»</strong> para escanear el buzón <code>facturacion@cocoand.com</code>.</p>
            </div>
          )}
          {recientes.map((f) => (
            <button
              key={f.id}
              data-testid={`overview-row-${f.id}`}
              onClick={() => onOpen(f)}
              className="w-full flex items-center justify-between px-4 sm:px-5 py-3 text-left hover:bg-slate-50 transition-colors gap-3"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-800 truncate">
                  {f.proveedor_normalizado || f.proveedor}
                </div>
                <div className="text-xs text-slate-400 font-mono truncate">
                  {f.num_factura || "—"} · {f.fecha || "—"} · CIF: {f.cif_nif || "—"}
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                <span className="text-xs sm:text-sm font-semibold kpi-num">{eur(f.total)}</span>
                <EstadoBadge estado={f.estado} />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

